import React, { useEffect, useImperativeHandle, useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import Drawer from '@app/component/overlays/drawer/Drawer';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import _clone from 'lodash/clone';
import Utilities from '@util/Utilities';
import CompanyHotelDashboardFilterArrangement
	from '@app/model/company_hotel/settings/CompanyHotelDashboardFilterArrangement';
import _isEmpty from 'lodash/isEmpty';
import CompanyHotelDashboardFilter from '@app/model/company_hotel/settings/CompanyHotelDashboardFilter';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
	closestCorners,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import _set from 'lodash/set';
import _get from 'lodash/get';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import FormButton from '@app/component/form/button/FormButton';
import CalendarFiltersSettings from '@app/model/company_hotel/settings/CalendarFiltersSettings';
import {
	useSaveCompanyHotelCalendarFiltersSettingsMutation
} from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';

const filterNames: Map<CompanyHotelDashboardFilter, string> = new Map([
	[CompanyHotelDashboardFilter.RATE, 'Member Rates'],
	[CompanyHotelDashboardFilter.NIGHTS, 'Number of Nights'],
	[CompanyHotelDashboardFilter.GUESTS, 'Minimum Occupancy'],
	[CompanyHotelDashboardFilter.ROOM, 'Room Type'],
	[CompanyHotelDashboardFilter.PLATFORM, 'Source (Devices)'],
	[CompanyHotelDashboardFilter.PROVIDER, 'Source (Channels)'],
	[CompanyHotelDashboardFilter.FLEX, 'Rate Category'],
	[CompanyHotelDashboardFilter.MEAL, 'Meal'],
	[CompanyHotelDashboardFilter.REGION, 'Region'],
	[CompanyHotelDashboardFilter.AIRPORT_CODE, 'Airport Code'],
	[CompanyHotelDashboardFilter.COMPETITOR_TAGS, 'Competitor Groups'],
]);


const DashboardHeaderManagementFiltersDrawer: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	onSave: (input: CalendarFiltersSettings) => any
	ref: any
}>
	= React.forwardRef(({ companyHotel, onSave }, ref: React.Ref<any>) => {

	const drawer = useDrawer();
	const toaster = useToaster();
	const { t } = useTranslation();

	const [items, setItems] = useState<{
		row0: CompanyHotelDashboardFilterArrangement[],
		row1: CompanyHotelDashboardFilterArrangement[]
	}>({ row0: [], row1: [] });
	const [draggedItem, setDraggedItem] = useState<CompanyHotelDashboardFilterArrangement | undefined>(undefined);

	const saveCompanyHotelCalendarFiltersMutation = useSaveCompanyHotelCalendarFiltersSettingsMutation({
		onError: (e: string) => {
			toaster.error('Filters', t(e));
		},
	});

	useImperativeHandle(ref, () => {
		return {
			open: () => {
				drawer.open();
			},
		};
	});

	useEffect(() => {
		loadItems();
	}, [companyHotel]);

	const loadItems = () => {
		setItems({
			row0: companyHotel.settings.calendarFilters.filtersArrangement.filter(arr => arr.row === 0).map(arr => _clone(arr)),
			row1: companyHotel.settings.calendarFilters.filtersArrangement.filter(arr => arr.row === 1).map(arr => _clone(arr)),
		});
	};

	const save = () => {
		companyHotel.settings.calendarFilters.filtersArrangement = [...items.row0, ...items.row1];
		onSave(companyHotel.settings.calendarFilters);
	};

	const findItemContainerId = (element: any) => {
		return element?.data.current?.sortable?.containerId;
	};

	const handleDragStart = (event: any) => {
		const { active } = event;

		const containerItems: CompanyHotelDashboardFilterArrangement[] = _get(items, findItemContainerId(active));
		if (!!containerItems) {
			const item = containerItems.find(arr => arr.filter === active.id);
			setDraggedItem(item);
		}
	};

	const handleDragOver = (event: any) => {
		const { active, over, draggingRect } = event;
		if (!!over) {

			const { id } = active;

			// Find the containers
			const activeContainer = findItemContainerId(active);
			const overContainer = findItemContainerId(over) || (activeContainer === 'row0' ? 'row1' : 'row0');

			if (activeContainer && activeContainer !== overContainer && !draggedItem?.isLockedInRow) {
				const targetRow = overContainer === 'row0' ? 0 : 1;

				const copy = _clone(items);
				const activeItems: CompanyHotelDashboardFilterArrangement[] = _get(copy, activeContainer);
				const overItems: CompanyHotelDashboardFilterArrangement[] = _get(copy, overContainer);
				if (overItems.length < 7) {
					draggedItem!.row = targetRow;
					const activeIndex = activeItems.findIndex(arr => arr.filter === id);

					overItems.push(draggedItem!);
					activeItems.splice(activeIndex, 1);

					setItems(copy);
				}
			}
		}
	};

	const handleDragEnd = (event: any) => {
		const { active, over } = event;


		const activeContainer = findItemContainerId(active);
		const overContainer = findItemContainerId(over);

		if (
			!activeContainer ||
			!overContainer ||
			activeContainer !== overContainer
		) {
			return;
		}
		const copy = _clone(items);
		const activeItems: CompanyHotelDashboardFilterArrangement[] = _get(copy, activeContainer);
		const overItems: CompanyHotelDashboardFilterArrangement[] = _get(copy, overContainer);

		const activeIndex = activeItems.findIndex(arr => arr.filter === active.id);
		const overIndex = overItems.findIndex(arr => arr.filter === over.id);


		if (activeIndex !== overIndex) {

			const sortedItems = arrayMove(_get(items, overContainer), activeIndex, overIndex);
			sortedItems.forEach((item: CompanyHotelDashboardFilterArrangement, index) => item!.position = index);

			setItems((items) => ({
				...items,
				[overContainer]: sortedItems,
			}));
		}

		setDraggedItem(undefined);
	};

	const updateItems = (rowItems: CompanyHotelDashboardFilterArrangement[], row: number) => {
		const copy = _clone(items);
		_set(copy, `row${row}`, rowItems);
		setItems(copy);
	};

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	return <Drawer title="Manage Filters" ref={drawer.ref} actions={
		<FormButton state="primary" isLoading={saveCompanyHotelCalendarFiltersMutation.isPending}
								onClick={save}>
			Submit
		</FormButton>
	}>

		<DndContext onDragEnd={handleDragEnd}
								onDragStart={handleDragStart}
								onDragOver={handleDragOver}
								collisionDetection={closestCorners}
								sensors={sensors}
								onDragAbort={() => setDraggedItem(undefined)}
								onDragCancel={() => setDraggedItem(undefined)}
		>
			<div className="mt-2 px-6 py-5 space-y-6">

				<div className="text-sm text-pretty">
					<p className="font-semibold tracking-tight text-gray-900">You can configure your filters into Primary and
						Secondary categories:</p>
					<ul className="mt-2 ml-4 list-disc space-y-1 text-gray-500">
						<li>Primary filters are always visible for quick access.</li>
						<li>Secondary filters are hidden by default and can be accessed through the dropdown button. Also they can
							be
							set to always on mode.
						</li>
						<li>Use Hide/Show to hide completely a filter.</li>
					</ul>
				</div>
				<RenderFiltersArea id="row0" items={items.row0} row={0} title="Primary Filters" draggedItem={draggedItem}
													 onChange={items => updateItems(items, 0)} />
				<RenderFiltersArea id="row1" items={items.row1} row={1} title="Secondary Filters" draggedItem={draggedItem}
													 onChange={items => updateItems(items, 1)} />
				<DragOverlay>{draggedItem ? <RenderFilterElement item={draggedItem} withShadow onToggleVisibility={() => {
				}} /> : null}</DragOverlay>
			</div>
		</DndContext>
	</Drawer>
		;
});

const RenderFiltersArea: React.FunctionComponent<{
	id: string,
	items: CompanyHotelDashboardFilterArrangement[],
	draggedItem: CompanyHotelDashboardFilterArrangement | undefined,
	row: number,
	title: string,
	onChange: (filters: CompanyHotelDashboardFilterArrangement[]) => void
}> = ({ id, items, row, title, onChange, draggedItem }) => {

	const toggleVisibility = (filter: string) => {
		const copy = _clone(items);
		const index = copy.findIndex(f => f.filter === filter);
		if (!copy[index].isLockedInRow) {
			copy[index].isHidden = !copy[index].isHidden;
			onChange(copy);
		}
	};

	const { setNodeRef } = useDroppable({
		id: id,
	});

	return <div
		className={Utilities.classNames('mt-3 space-y-2 text-gray-800 min-h-[300px]', row === 1 && !!draggedItem && draggedItem.isLockedInRow && 'opacity-50')}>
		<div
			className={'divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10'}>
			<div className="px-4 py-3"><h6 className="text-sm font-semibold text-gray-900">{title}</h6></div>
			<SortableContext id={id} items={items.map(f => f.filter) || []} strategy={verticalListSortingStrategy}>
				<div ref={setNodeRef} className="px-4 py-5 space-y-2 min-h-[300px]">
					{!_isEmpty(items) && items.filter(arr => arr.row === row)
						.map(arrangement => <RenderFilterElement key={arrangement.filter} item={arrangement}
																										 onToggleVisibility={toggleVisibility}
																										 opacity={!!draggedItem && draggedItem.filter === arrangement.filter ? 0.5 : 1} />)}
					{_isEmpty(items) && <p className="text-sm text-gray-500">No filters defined</p>}
				</div>
			</SortableContext>
		</div>
	</div>;
};

const RenderFilterElement: React.FunctionComponent<{
	item: CompanyHotelDashboardFilterArrangement,
	onToggleVisibility: (filter: CompanyHotelDashboardFilter) => void,
	opacity?: number,
	withShadow?: boolean

}> = ({ item, onToggleVisibility, opacity = 1, withShadow = false }) => {

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
	} = useSortable({ id: item.filter });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity,
		boxShadow: withShadow ? ' 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' : 'none',
	};

	return <div id={item.filter} ref={setNodeRef} style={style}
							className={Utilities.classNames('flex items-center justify-between rounded-md px-3 py-2 ring-1 ring-black ring-opacity-5 text-xs', item.isHidden ? 'bg-gray-100' : 'bg-white')}>
		<span className="flex items-center">
			<button {...attributes} {...listeners}>
				<img
					className="block h-5 w-auto cursor-grab mr-2"
					src="/assets/img/grid-dots.svg"
					alt="RoomPulse"
				/>
			</button>
			<span className="font-medium, text-gray-700">
				{filterNames.get(item.filter) || item.filter}
			</span>
		</span>
		{!item.isLockedInRow && <a className="text-blue-600 font-semibold hover:text-blue-900 cursor-pointer"
															 title={item.isHidden ? 'Show Filter' : 'Hide Filter'}
															 onClick={() => onToggleVisibility(item.filter)}>{item.isHidden ? 'Show' : 'Hide'}
		</a>}
	</div>;
};

export default DashboardHeaderManagementFiltersDrawer;